import React, { useEffect, useState } from 'react';
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { Button } from '../../Core';
import { EquipmentDialog } from './EquipmentDialog';
import './EquipmentStatus.scss';

interface IEquipmentIconProps {
  focusRef?: React.Ref<HTMLButtonElement>;
  importMismatch?: boolean;
  noPadding?: boolean;
  onClick?: (event: any) => void;
  outOfCalibration?: boolean;
}

const EquipmentIcon: React.FC<IEquipmentIconProps> = ({
  focusRef,
  importMismatch,
  noPadding,
  onClick,
  outOfCalibration,
}) => {
  if (outOfCalibration === true || importMismatch === true) {
    return (
      <Button
        className="EquipmentStatus-Icon"
        alwaysEnabled
        subtle
        warning
        noPadding={noPadding}
        onClick={onClick}
        focusRef={focusRef}
      >
        <FaExclamationTriangle size="1.5em" />
      </Button>
    );
  } else {
    return (
      <Button
        className="EquipmentStatus-Icon"
        alwaysEnabled
        subtle
        noPadding={noPadding}
        onClick={onClick}
        focusRef={focusRef}
      >
        <FaInfoCircle size="1.5em" />
      </Button>
    );
  }
};

export interface IEquipmentStatusProps {
  equipment?: any;
  equipmentType: string;
  focusRef?: React.Ref<HTMLButtonElement>;
  importAction?: any;
  importedAdditionalData?: any;
  importedCalibration?: any;
  importModel?: any;
  noPadding?: boolean;
  status?: any;
  statusDate?: any;
  warningIconOnly?: boolean;
  importing?: boolean;
}

const EquipmentStatus: React.FC<IEquipmentStatusProps> = ({
  equipment,
  equipmentType,
  focusRef,
  importAction,
  importedAdditionalData,
  importedCalibration,
  importModel,
  noPadding,
  status,
  statusDate,
  importing,
  warningIconOnly = false,
}) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [closeDialogAfterImport, setCloseDialogAfterImport] = useState(false);

  useEffect(() => {
    if (!importing && closeDialogAfterImport) {
      setDialogVisible(false);
      setCloseDialogAfterImport(false);
    }
  }, [importing, closeDialogAfterImport]);

  if (!equipment) return null;
  if (!status)
    return (
      <div className="EquipmentStatus">
        <Button subtle loading />
      </div>
    );

  const importMismatch =
    (importedCalibration && importedCalibration.Mismatch) ||
    (importedAdditionalData && importedAdditionalData.Mismatch);
  return (
    <>
      <div className="EquipmentStatus">
        {
          /* If in warning icon only mode, only show icon if it is a warning */
          !(warningIconOnly && status.InCalibration && !importMismatch) && (
            <EquipmentIcon
              focusRef={focusRef}
              importMismatch={importMismatch}
              noPadding={noPadding}
              onClick={() => setDialogVisible(true)}
              outOfCalibration={status.OutOfCalibration}
            />
          )
        }
      </div>
      <EquipmentDialog
        equipment={equipment}
        equipmentType={equipmentType}
        importAction={importAction}
        importedAdditionalData={importedAdditionalData}
        importedCalibration={importedCalibration}
        importModel={importModel}
        importing={importing}
        onClose={() => setCloseDialogAfterImport(true)}
        status={status}
        statusDate={statusDate}
        visible={dialogVisible}
      />
    </>
  );
};

export { EquipmentStatus };
export default EquipmentStatus;
